/**
 * ⭐ T2075 M5 (plan Step 2, D11, A4) — THE BROWSER HALF OF THE CSV IMPORT:
 * decode the chosen file, sniff the delimiter off the header, find the `name`
 * and `group` columns (synonyms accepted), report every other column as
 * ignored, and hand back rows shaped for `POST /api/admin/staff/import`.
 *
 * ⛔ THE BROWSER PARSES, THE SERVER VALIDATES (D11). Nothing here decides
 * whether a cell may be stored: `staff-repo.ts` cleans, caps and refuses every
 * cell again, so a doctored `fetch` that skips this module changes nothing.
 * What this module DOES decide is the row cap — a file over
 * `STAFF_IMPORT_MAX_ROWS` rows is refused HERE with the count, before a byte is
 * posted, because a 40 MB export is not a roster and the server's body cap
 * would answer it with a bare 413 rather than a sentence.
 *
 * ⛔ NO PHONE, NO EMAIL, NO ROLE (A4, S13): whatever an Excel export carries
 * beside the two columns is named in `ignoredColumns` and never leaves the
 * browser. A `role` or `email` column therefore cannot create an account.
 *
 * Decoding: a UTF-8 BOM or a UTF-16LE BOM is honoured; otherwise UTF-8 is
 * tried FATALLY and `windows-1252` (what Excel on Windows writes without a
 * BOM) is the fallback, so a fada survives both ways a home is likely to
 * produce the file. The encoding used is reported so the admin can see why a
 * name looked wrong.
 */
import { parseCsvText, STAFF_IMPORT_MAX_ROWS, type CsvRecord } from '../core/csv.js';

export type DecodedCsv = {
  text: string;
  encoding: 'utf-8' | 'utf-16le' | 'windows-1252';
  delimiter: ',' | ';' | '\t';
};

/** The header words that mean the name column and the group column, after
 *  normalisation (trim, lower, one space). `staff` and `full name` are what a
 *  home's HR export calls the name; `team` and `ward` are what it calls the
 *  group. */
export const NAME_HEADERS: readonly string[] = ['name', 'staff', 'full name', 'staff name', 'display name'];
export const GROUP_HEADERS: readonly string[] = ['group', 'team', 'ward', 'unit'];
/** ⭐ T2173 — the header word that means the active flag, so an EXPORT ROUND-TRIPS.
 *  `adminExportStaff` writes the header `Active` and the cell `yes` / `no`
 *  (`server/admin-routes.ts`), which `normaliseHeader` folds to `active`; the
 *  alias is the one an HR export is likely to use for the same column.
 *  ⛔ ONE WORD, NOT A FAMILY: `name` and `group` carry several aliases because a
 *  home's own spreadsheet names them freely, but this column exists to read OUR
 *  export back, and a guessed alias on a state-changing column is a guess about
 *  whether a nurse appears on a picker. A home that wants it types our word. */
export const ACTIVE_HEADERS: readonly string[] = ['active'];

export function normaliseHeader(cell: string): string {
  return cell.replace(/^\uFEFF/, '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** The delimiter the header uses: the one of `,` `;` and tab that appears most
 *  on the first line, comma on a tie or when none appears. */
export function sniffDelimiter(firstLine: string): ',' | ';' | '\t' {
  const counts: Array<[',' | ';' | '\t', number]> = [
    [',', (firstLine.match(/,/g) ?? []).length],
    [';', (firstLine.match(/;/g) ?? []).length],
    ['\t', (firstLine.match(/\t/g) ?? []).length],
  ];
  let best = counts[0]!;
  for (const c of counts) if (c[1] > best[1]) best = c;
  return best[0];
}

/** Decode bytes: BOM sniff, fatal UTF-8, then windows-1252. */
export function decodeCsvBytes(bytes: ArrayBuffer): { text: string; encoding: DecodedCsv['encoding'] } {
  const view = new Uint8Array(bytes);
  if (view.length >= 2 && view[0] === 0xff && view[1] === 0xfe) {
    return { text: new TextDecoder('utf-16le').decode(view.subarray(2)), encoding: 'utf-16le' };
  }
  if (view.length >= 3 && view[0] === 0xef && view[1] === 0xbb && view[2] === 0xbf) {
    return { text: new TextDecoder('utf-8').decode(view.subarray(3)), encoding: 'utf-8' };
  }
  try {
    return { text: new TextDecoder('utf-8', { fatal: true }).decode(view), encoding: 'utf-8' };
  } catch {
    return { text: new TextDecoder('windows-1252').decode(view), encoding: 'windows-1252' };
  }
}

export type CsvImportPlan = {
  encoding: DecodedCsv['encoding'];
  delimiter: DecodedCsv['delimiter'];
  /** Header cells (as written) that are none of the name, the group and Active. */
  ignoredColumns: string[];
  /** The rows to post: 1-indexed SOURCE line, the cells as read.
   *  ⭐ T2173 — `active` is the RAW CELL, a string, and is `undefined` when the
   *  file has no Active column or the cell is blank (both mean "leave it alone").
   *  ⛔ IT IS NOT PARSED HERE, and that is D11 above, not an omission: deciding
   *  that `maybe` is unstorable is a validation, and validations live in
   *  `core/staff-repo.ts`, which refuses BY LINE and lets the rest of the file
   *  land. This module's only refusal grammar is whole-file, so a verdict taken
   *  here would kill a 300-row roster over one mistyped cell. */
  rows: Array<{ line: number; name: string; group: string; active?: string }>;
};

export type CsvImportRefusal =
  | { kind: 'empty' }
  | { kind: 'no_name_column'; headers: string[] }
  | { kind: 'no_group_column'; headers: string[] }
  | { kind: 'too_many_rows'; rows: number; max: number };

/**
 * Turn decoded text into the rows to post, or a refusal the screen can word.
 * Pure, so `csv.spec.ts` drives it without a `File`.
 */
export function planCsvImport(decoded: { text: string; encoding: DecodedCsv['encoding'] }): CsvImportPlan | CsvImportRefusal {
  const firstLine = decoded.text.split(/\r\n|\n|\r/, 1)[0] ?? '';
  const delimiter = sniffDelimiter(firstLine);
  const records: CsvRecord[] = parseCsvText(decoded.text, delimiter);
  const header = records[0];
  if (header === undefined || header.cells.every((c) => c.trim() === '')) return { kind: 'empty' };
  const headers = header.cells;
  const normalised = headers.map(normaliseHeader);
  const nameAt = normalised.findIndex((h) => NAME_HEADERS.includes(h));
  if (nameAt === -1) return { kind: 'no_name_column', headers };
  const groupAt = normalised.findIndex((h, i) => i !== nameAt && GROUP_HEADERS.includes(h));
  if (groupAt === -1) return { kind: 'no_group_column', headers };
  // ⭐ T2173 — Active is OPTIONAL: -1 means the file has none, which the row map
  // below turns into `undefined` on every row, i.e. "leave every row's state
  // alone". It also leaves `ignoredColumns`, so the report stops naming a column
  // it now reads.
  const activeAt = normalised.findIndex((h, i) => i !== nameAt && i !== groupAt && ACTIVE_HEADERS.includes(h));
  const ignoredColumns = headers
    .filter((_h, i) => i !== nameAt && i !== groupAt && i !== activeAt)
    .map((h) => h.trim())
    .filter((h) => h !== '');
  const body = records.slice(1);
  if (body.length > STAFF_IMPORT_MAX_ROWS) {
    return { kind: 'too_many_rows', rows: body.length, max: STAFF_IMPORT_MAX_ROWS };
  }
  return {
    encoding: decoded.encoding,
    delimiter,
    ignoredColumns,
    rows: body.map((r) => {
      const row: { line: number; name: string; group: string; active?: string } = {
        line: r.line,
        name: r.cells[nameAt] ?? '',
        group: r.cells[groupAt] ?? '',
      };
      // A blank cell is "unchanged", exactly as an absent column is, so the key
      // is OMITTED rather than sent as '' — the wire then carries no claim at all.
      const raw = activeAt === -1 ? '' : (r.cells[activeAt] ?? '').trim();
      if (raw !== '') row.active = raw;
      return row;
    }),
  };
}

/** `File` → the plan. The one function the screen calls. */
export async function decodeCsvFile(file: File): Promise<CsvImportPlan | CsvImportRefusal> {
  const bytes = await file.arrayBuffer();
  return planCsvImport(decodeCsvBytes(bytes));
}
